import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { updateResource } from "@/lib/api";

// Click a note (or "Add note") to edit it inline without opening the full edit form.
export default function NoteCell({ row, path = "workorders", field = "notes" }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(row[field] || "");
  const [saving, setSaving] = useState(false);
  const hasNote = !!(row[field] || "").trim();

  const save = async () => {
    setSaving(true);
    try {
      await updateResource(path, row.id, { [field]: text.trim() });
      qc.invalidateQueries({ queryKey: [path] });
      toast.success("Note saved");
      setOpen(false);
    } catch {
      toast.error("Couldn't save note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setText(row[field] || ""); }}>
      <PopoverTrigger asChild>
        <button data-testid="note-cell-trigger" className="text-left focus:outline-none group max-w-[240px]">
          {hasNote ? (
            <span className="text-sm text-gray-600 group-hover:text-gray-900 group-hover:underline decoration-dotted">{row[field]}</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-sm text-gray-400 group-hover:text-gray-700">
              <Pencil className="w-3.5 h-3.5" /> Add note
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="rounded-none border-2 border-gray-900 w-80">
        <div className="space-y-3">
          <Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Note</Label>
          <Textarea
            data-testid="note-input"
            autoFocus
            rows={4}
            className="rounded-none border-2"
            value={text}
            placeholder="e.g. Doors were measured; waiting on quote"
            onChange={(e) => setText(e.target.value)}
          />
          <Button
            data-testid="note-save-btn"
            onClick={save}
            disabled={saving}
            className="w-full rounded-none bg-gray-900 hover:bg-gray-700 text-white font-bold uppercase"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save Note
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
